/**
 * Closest approach to a body, read off the two-body conic the ship is on in
 * that body's own frame.
 *
 * The state handed in is RELATIVE to the body - position and velocity both -
 * so the body's own heliocentric travel is divided out before any of this
 * runs, the same move `src/main.ts`'s frame parent makes for the flight step.
 * The body-centred frame is treated as inertial for the forecast: near the
 * body its own pull is the dominant term by definition, and far from it the
 * `mu/r` terms fall away and the conic degenerates to the straight-line
 * closest approach of the relative motion, which is the right answer for a
 * flyby that misses by millions of kilometres. Between the two, at the
 * dominance boundary, neither term leads and the predicted trajectory line is
 * the fuller answer.
 */

/** Written in place: nothing here allocates, so a frame loop may call it. */
export interface ClosestApproach {
  /** False when the state carries no conic to read at all. */
  ok: boolean;
  /** Periapsis radius from the body's centre, metres. */
  radiusM: number;
  /** Seconds to that periapsis. Negative once it is behind the ship. */
  timeS: number;
}

/**
 * How near the specific energy may sit to zero before the conic is refused.
 * A parabola has no semi-major axis, and every anomaly below divides by one.
 * Measured against `mu/r` so the test is scale-free.
 */
const PARABOLIC_FRACTION = 1e-9;
/** Below this eccentricity the orbit is circular and every point is periapsis. */
const CIRCULAR_E = 1e-9;
/** `x` folded into [0, 2 pi). */
const wrap2Pi = (x: number): number => ((x % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

export function makeClosestApproach(): ClosestApproach {
  return { ok: false, radiusM: 0, timeS: 0 };
}

/**
 * `rx..rz` is the ship's position relative to the body, `vx..vz` its velocity
 * relative to the body, `mu` the body's own GM. `linear` drops gravity and
 * reads the straight-line closest approach instead, which is the control the
 * conic is checked against.
 */
export function closestApproach(
  rx: number, ry: number, rz: number,
  vx: number, vy: number, vz: number,
  mu: number, linear: boolean, out: ClosestApproach,
): ClosestApproach {
  out.ok = false;
  out.radiusM = 0;
  out.timeS = 0;
  const r = Math.hypot(rx, ry, rz);
  const v2 = vx * vx + vy * vy + vz * vz;
  if (!(r > 0) || !(v2 > 0)) return out;
  const rv = rx * vx + ry * vy + rz * vz;

  if (linear || !(mu > 0)) {
    const t = -rv / v2;
    out.timeS = t;
    out.radiusM = Math.hypot(rx + vx * t, ry + vy * t, rz + vz * t);
    out.ok = true;
    return out;
  }

  const eps = v2 / 2 - mu / r;
  if (Math.abs(eps) < PARABOLIC_FRACTION * (mu / r)) return out;
  const a = -mu / (2 * eps);
  const k = v2 - mu / r;
  const ex = (k * rx - rv * vx) / mu;
  const ey = (k * ry - rv * vy) / mu;
  const ez = (k * rz - rv * vz) / mu;
  const e = Math.hypot(ex, ey, ez);
  out.radiusM = Math.max(0, a * (1 - e));

  if (e < CIRCULAR_E) {
    out.timeS = 0;
    out.ok = true;
    return out;
  }
  if (eps < 0) {
    const cosE = (1 - r / a) / e;
    const sinE = rv / (e * Math.sqrt(mu * a));
    const anomaly = Math.atan2(sinE, cosE);
    const meanAnomaly = anomaly - e * Math.sin(anomaly);
    const n = Math.sqrt(mu / (a * a * a));
    out.timeS = meanAnomaly <= 0 ? -meanAnomaly / n : (2 * Math.PI - meanAnomaly) / n;
  } else {
    const negA = -a;
    const cosh = Math.max(1, (1 - r / a) / e);
    const anomaly = rv < 0 ? -Math.acosh(cosh) : Math.acosh(cosh);
    const meanAnomaly = e * Math.sinh(anomaly) - anomaly;
    const n = Math.sqrt(mu / (negA * negA * negA));
    out.timeS = -meanAnomaly / n;
  }
  out.ok = Number.isFinite(out.timeS) && Number.isFinite(out.radiusM);
  return out;
}

/** Seconds of full thrust to take a relative speed to zero. Null with no thrust. */
export function burnToZeroSeconds(speedMps: number, accelMps2: number): number | null {
  if (!(accelMps2 > 0) || !Number.isFinite(speedMps)) return null;
  return speedMps / accelMps2;
}

/** Written in place beside `ClosestApproach`: nothing below allocates either. */
export interface BrakeCue {
  /** False when nothing is owed, or when there is no countdown to read. */
  ok: boolean;
  /** Seconds until retrograde thrust has to start. Zero and below is now. */
  startS: number;
  /** Seconds of full thrust the arrival costs. */
  burnS: number;
  /** The speed the arrival is aimed at, metres a second. */
  targetMps: number;
  /** Seconds of countdown left when the cue should start warning. */
  marginS: number;
  /** True when `startS` is the two-body conic's own leave time rather than the
   *  drawn path's flown one - it ignores every pull but the target's own. */
  approx: boolean;
  /** True when nothing is owed because the body already holds the ship: a
   *  closed orbit clear of the ground that stays inside its hold. */
  held: boolean;
}

export function makeBrakeCue(): BrakeCue {
  return { ok: false, startS: 0, burnS: 0, targetMps: 0, marginS: 0, approx: false, held: false };
}

/**
 * When retrograde thrust has to start for a ship coasting on the conic
 * `closestApproach` just read to reach that periapsis at the speed of a
 * circular orbit there.
 *
 * `rM` and `speedMps` are the ship's distance and speed relative to the body,
 * `periapsisM` and `timeToPeriapsisS` that conic's own answer, `surfaceM` the
 * surface under the ship - the drawn ground on a body whose globe is its own
 * shape, the registry radius on one drawn as a sphere - which is what decides
 * whether the periapsis clears the ground at all. `zeroTarget` is the state
 * with nothing to arrive ON - no mass, the straight-line reading, a periapsis
 * inside the body - where the target is rest instead.
 *
 * `holdRadiusM` is how far out this body still out-pulls the one it orbits.
 * A closed conic that clears the surface and stays inside it is a capture the
 * pilot already has, so nothing is owed and the cue reads quiet whatever the
 * eccentricity; open, or the periapsis at or below the surface, and the
 * countdown to that periapsis is real. A closed conic reaching OUTSIDE the
 * hold is neither: the body stops holding the ship before the periapsis it
 * would otherwise count down to comes round again, so what is read is the
 * time to that crossing instead. Zero is "nothing here is secure", which is
 * what `src/main.ts` passes while the throttle is open and for the control
 * that reproduces the always-owed read.
 */
export function brakeCue(
  rM: number, speedMps: number,
  periapsisM: number, timeToPeriapsisS: number,
  mu: number, surfaceM: number, holdRadiusM: number,
  accelMps2: number, turnRateRadS: number,
  zeroTarget: boolean, leaveTimeOff: boolean, out: BrakeCue,
): BrakeCue {
  out.ok = false;
  out.startS = 0;
  out.burnS = 0;
  out.targetMps = 0;
  out.marginS = 0;
  out.approx = false;
  out.held = false;
  if (!(accelMps2 > 0) || !(rM > 0) || !Number.isFinite(speedMps)) return out;
  if (!Number.isFinite(timeToPeriapsisS)) return out;
  if (timeToPeriapsisS < 0) {
    // Past periapsis and opening: no arrival left on this pass, but the
    // crossing out of the hold is still owed, on the open conic's own anomaly.
    if (zeroTarget || leaveTimeOff || !(mu > 0)) return out;
    if (!(holdRadiusM > 0) || !(rM < holdRadiusM)) return out;
    const epsOut = speedMps * speedMps / 2 - mu / rM;
    if (!(epsOut > 0)) return out;
    const negA = mu / (2 * epsOut);
    const eOut = 1 + periapsisM / negA;
    if (!(eOut > 1)) return out;
    const hNow = Math.acosh(Math.max(1, (rM / negA + 1) / eOut));
    const hOut = Math.acosh(Math.max(1, (holdRadiusM / negA + 1) / eOut));
    const leaveS = ((eOut * Math.sinh(hOut) - hOut) - (eOut * Math.sinh(hNow) - hNow))
      / Math.sqrt(mu / (negA * negA * negA));
    if (!(leaveS >= 0) || !Number.isFinite(leaveS)) return out;
    out.startS = leaveS;
    out.ok = true;
    out.approx = true;
    return out;
  }
  const orbital = !zeroTarget && mu > 0 && periapsisM > surfaceM;
  if (orbital && holdRadiusM > 0) {
    // Arrival speed at periapsis beats circular speed there for ANY closed
    // eccentricity above zero, so owing a burn on that alone told a parked
    // ship to brake an orbit from now for a fraction of a second of thrust.
    // Energy is invariant along the conic, so the apoapsis follows from the
    // CURRENT r and speed and the periapsis closestApproach already found.
    const eps = speedMps * speedMps / 2 - mu / rM;
    if (eps < 0) {
      const a = -mu / (2 * eps);
      const apoapsisM = 2 * a - periapsisM;
      if (apoapsisM <= holdRadiusM) {
        out.held = true;
        return out;
      }
      // The body stops holding the ship before periapsis comes round again, and
      // `src/sim/trajectory.ts` calls LEAVES at the same crossing. This closed
      // form freezes the hold radius the forecast re-walks, so the two part by a
      // few percent near in and up to double near a Hill-sphere share.
      if (!leaveTimeOff) {
        const e = 1 - periapsisM / a;
        const cosHoldE = Math.min(1, Math.max(-1, (1 - holdRadiusM / a) / e));
        const holdE = Math.acos(cosHoldE);
        const holdMeanAnomaly = holdE - e * Math.sin(holdE);
        const n = Math.sqrt(mu / (a * a * a));
        const nowMeanAnomaly = wrap2Pi(2 * Math.PI - n * timeToPeriapsisS);
        out.startS = wrap2Pi(holdMeanAnomaly - nowMeanAnomaly) / n;
        out.ok = true;
        out.approx = true;
        return out;
      }
    }
  }
  const targetMps = orbital ? Math.sqrt(mu / periapsisM) : 0;
  // Energy on the coasting conic, which is the speed the arrival is reached at
  // with nothing done about it.
  const arrivalMps = orbital
    ? Math.sqrt(Math.max(0, speedMps * speedMps + 2 * mu * (1 / periapsisM - 1 / rM)))
    : speedMps;
  if (!(arrivalMps > targetMps)) return out;
  const burnS = (arrivalMps - targetMps) / accelMps2;
  if (!Number.isFinite(burnS)) return out;
  out.targetMps = targetMps;
  out.burnS = burnS;
  // A burn of length T is an impulse at its MIDPOINT, so an arrival in orbit
  // straddles the periapsis rather than braking early enough to finish there:
  // a retrograde burn sheds angular momentum where the flight path angle is
  // not yet zero, which drops the periapsis out from under the answer - into
  // the ground on a fast approach. An arrival at REST has no periapsis to
  // straddle and has to be reached AT the closest approach, not through it.
  out.startS = timeToPeriapsisS - (orbital ? burnS / 2 : burnS);
  // The countdown's own error is a share of the burn, so the warning band is
  // one too; floored at the 180 degree flip, since a warning shorter than the
  // turn cannot be acted on.
  out.marginS = Math.max(burnS / 2, turnRateRadS > 0 ? Math.PI / turnRateRadS : 0);
  out.ok = true;
  return out;
}

/** The far end of the drawn prediction, as BRAKE reads it. `budget` is not one:
 *  a forecast that ran out of horizon has said nothing about the path. */
export type BrakePathEnd = 'ground' | 'closed' | 'left';

/**
 * The same cue read off the drawn line's own far end instead of the osculating
 * conic. While the throttle is open that forecast flies the burn ON for as
 * long as the pilot holds it, which the conic `brakeCue` reads cannot see, so
 * the two disagree about whether the path leaves or lands until the throttle
 * shuts - and the caption the pilot is looking at is the forecast's. `atS` is
 * that end's own seconds as the caption prints them, uncorrected for the time
 * since the build, so the row and the caption cannot drift apart.
 */
export function brakeCueFromPath(
  reason: BrakePathEnd, atS: number, speedMps: number,
  accelMps2: number, turnRateRadS: number, out: BrakeCue,
): BrakeCue {
  out.ok = false;
  out.startS = 0;
  out.burnS = 0;
  out.targetMps = 0;
  out.marginS = 0;
  out.approx = false;
  out.held = false;
  if (!(accelMps2 > 0) || !Number.isFinite(speedMps)) return out;
  if (!Number.isFinite(atS) || atS < 0) return out;
  // A capture the body keeps owes nothing, whatever the eccentricity.
  if (reason === 'closed') {
    out.held = true;
    return out;
  }
  if (reason === 'left') {
    // Nothing to size: the crossing is the whole reading, the way the leave
    // branch above leaves the burn and the margin at zero.
    out.startS = atS;
    out.ok = true;
    return out;
  }
  if (!(speedMps > 0)) return out;
  // Ground is arrived at REST, so the burn is the whole relative speed and it
  // has to finish there rather than straddle it.
  out.burnS = speedMps / accelMps2;
  out.startS = atS - out.burnS;
  out.marginS = Math.max(out.burnS / 2, turnRateRadS > 0 ? Math.PI / turnRateRadS : 0);
  out.ok = true;
  return out;
}
